<script lang="ts">
  import type { GetProductQuery } from '$gql/storefront/operations';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { aboveLg } from '$lib/stores/breakpoints';

  import ShopifyImg from '$components/shopify-img.svelte';
  import Accordion from '$components/accordion.svelte';
  import { fade } from 'svelte/transition';

  import { DEFAULT_TITLE, DESKTOP_ALT, ProductMetafields } from '$lib/constants';
  import { cn } from '$lib/utils';
  import { buildSizes } from '$lib/image';

  import { reducedMotion } from '$lib/stores/reduced-motion';
  import type { ImageDescriptionStoryblok } from '$types/bloks';
  import type { ShopifyImage, ShopifyVideo } from '$types/shopify';
  import Link from '$components/ui/link.svelte';
  import PreviewMedia from './preview-media.svelte';
  import type { MediaImage } from '$gql/storefront/schemas';
  import { abTestVersion } from '$lib/stores/ab-test-version';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { shouldShowMediaItemAccordingToABTest } from '$lib/utils/ab-test-media';
  import { trimVariantId } from '$lib/utils/shopify/productsId';

  export let product: NonNullable<GetProductQuery['product']>;
  export let selectedOptions: Record<string, string>;
  export let selectedVariantId: string | undefined;
  export let imageDescription: ImageDescriptionStoryblok[] | undefined;
  export let showOutline: boolean | undefined = undefined;
  export let outlineTitle: string | undefined = undefined;
  export let isBlackOutlineColor: boolean | undefined = undefined;

  let className: string | undefined = undefined;

  export { className as class };

  const shouldParseVariantImages = !!product.metafields.filter(
    (meta) => meta?.key === ProductMetafields.VARIANT_IMAGES
  )[0]?.value;

  const options = product.options;
  const images = product.images.nodes;
  const width = 1000;
  let selectedMedia: ShopifyImage | string | undefined = undefined;
  let selectedMediaIdx: number | undefined = undefined;

  const DEFAULT_IMAGES_KEY = 'default' as const;

  // Images
  let imagesByVariant: Record<string, ShopifyImage[]> = {};
  let parsedImages = images;

  // Deprecated solution, use v-{variantId} in altText
  $: if (shouldParseVariantImages) {
    parsedImages = [
      ...new Set([
        ...options.map((opt) => imagesByVariant[selectedOptions[opt.name]] || []).flat(),
        ...imagesByVariant[DEFAULT_IMAGES_KEY]
      ])
    ];
  }

  if (shouldParseVariantImages) {
    const allOptions = [
      DEFAULT_IMAGES_KEY,
      ...options.map((opt) => opt.values.map((val) => val)).flat()
    ];

    let tmp: Record<string, ShopifyImage[]> = Object.fromEntries(
      allOptions.map((opt) => [opt, []])
    );

    images?.forEach((img) => {
      let wasIndexed = false;

      allOptions.forEach((opt) => {
        if (
          img.altText
            ?.replace(/\s/g, '')
            .toLowerCase()
            .includes(opt.replace(/\s/g, '').toLowerCase())
        ) {
          wasIndexed = true;
          tmp[opt] = [...tmp[opt], img];
        }
      });

      if (!wasIndexed) {
        tmp[DEFAULT_IMAGES_KEY] = [...tmp[DEFAULT_IMAGES_KEY], img];
      }
    });

    imagesByVariant = tmp;
  }

  const getImageDescription = (index: number): ImageDescriptionStoryblok | undefined => {
    if (imageDescription) {
      return imageDescription?.find((item) => +item.image_number === +index);
    }
  };

  $: filteredMedia = product.media.nodes
    .filter((item) => {
      return shouldShowMediaItemAccordingToABTest(item, $abTestVersion);
    })
    ?.map((p) => {
      if (p.__typename === 'MediaImage') {
        const imageObject = p as MediaImage;
        const result = parsedImages.find((item) => item.url === imageObject.previewImage?.url);
        return result ?? p;
      }
      return p;
    })
    .filter(
      (item) =>
        (item.__typename === 'Video' && item.alt && item.alt.includes(DESKTOP_ALT) && $aboveLg) ||
        (item.__typename === 'Video' && !item.alt && !$aboveLg) ||
        item.__typename !== 'Video'
    )
    .filter((item) => {
      if (selectedVariantId) {
        const variantId = trimVariantId(selectedVariantId);
        let itemVariantSpecific;
        if ('alt' in item) {
          itemVariantSpecific = item.alt?.includes('v-');

          if (!itemVariantSpecific) {
            return true;
          } else {
            return item.alt?.includes(`v-${variantId}`);
          }
        } else if ('altText' in item) {
          itemVariantSpecific = item.altText?.includes('v-');
          if (!itemVariantSpecific) {
            return true;
          } else {
            return item.altText?.includes(`v-${variantId}`);
          }
        }
      }
      return true;
    });

  // wait for abtestversion to be available if necessaru
  $: parsedMedia = filteredMedia;

  const onImageDescriptionInteract = (title: string) => {
    const selectedVariant = product.variants.nodes.find(
      (variant) => variant.title === Object.values(selectedOptions).join(' / ')
    );

    let productVariantName = selectedVariant?.title ?? '';

    if (!productVariantName) {
      const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
      productVariantName =
        availableVariant?.title && availableVariant?.title !== DEFAULT_TITLE
          ? availableVariant.title
          : product.title;
    }

    analyticsEvent({
      event: AnalyticsEvents.IMAGE_DESCRIPTION_MODULE,
      title,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          item_category: product.productType,
          item_variant: productVariantName
        }
      ]
    });
  };

  const getSrc = (sources: ShopifyVideo['sources']) => {
    const hd = sources.find((src) => src.url.includes('HD-720p'))?.url;
    if (hd) {
      return hd;
    } else {
      const mp4 = sources.find((src) => src.url.includes('.mp4'))?.url;
      return mp4;
    }
  };

  const onPreviewMedia = (media: ShopifyImage | string | undefined, idx?: number) => {
    selectedMedia = media;
    selectedMediaIdx = idx;

    document.body.style.overflow = 'hidden';
  };

  const onClosePreview = (event?: KeyboardEvent | undefined) => {
    if (event && event.key !== 'Escape') {
      return;
    }
    selectedMedia = undefined;
    selectedMediaIdx = undefined;

    document.body.style.overflow = 'visible';
  };

  const handleMediaCarousel = (to: 'left' | 'right') => {
    if (selectedMediaIdx !== undefined) {
      let newSelectedMediaIdx;
      if (to === 'left') {
        newSelectedMediaIdx = selectedMediaIdx - 1;
      } else {
        newSelectedMediaIdx = selectedMediaIdx + 1;
      }

      if (newSelectedMediaIdx > -1 && newSelectedMediaIdx < parsedMedia.length) {
        if (parsedMedia[newSelectedMediaIdx].__typename === 'Image') {
          selectedMedia = parsedMedia[newSelectedMediaIdx] as ShopifyImage;
        } else if (parsedMedia[newSelectedMediaIdx].__typename === 'Video') {
          const src = getSrc((parsedMedia[newSelectedMediaIdx] as ShopifyVideo).sources);

          selectedMedia = src;
        }
        selectedMediaIdx = newSelectedMediaIdx;
      }
    }
  };
</script>

<svelte:window on:keydown={onClosePreview} />

{#key parsedMedia}
  <div class={cn('relative w-full hidden lg:grid grid-cols-2 gap-4 lg:sticky h-max', className)}>
    <slot />
    {#if parsedMedia?.length > 0}
      {#each parsedMedia || [] as mediaItem, i}
        {#if mediaItem && mediaItem.__typename === 'Image'}
          {@const { url: src, altText: alt } = mediaItem}

          <div
            class={cn(
              'min-w-full select-none relative overflow-hidden rounded-md aspect-[7/9]',
              showOutline &&
                i == 0 &&
                (isBlackOutlineColor
                  ? 'border border-accent bg-accent'
                  : 'border border-[--outlineBackgroudColor] bg-[--outlineBackgroudColor]')
            )}
          >
            {#if showOutline && i == 0}
              <p class="py-1 px-1 text-center text-highlight text-xs font-medium">
                {outlineTitle}
              </p>
            {/if}
            {#if imageDescription}
              {@const info = getImageDescription(i + 1)}
              {#if info}
                <div
                  class="absolute right-2.5 top-2.5 z-10 w-fit"
                  transition:fade={{ duration: $reducedMotion ? 0 : 200 }}
                >
                  <Accordion
                    title={info.title}
                    variant="image-info"
                    class="transform-gpu backdrop-blur"
                    onInteract={() => {
                      onImageDescriptionInteract && onImageDescriptionInteract(info.title);
                    }}
                  >
                    <div class="border-t border-primary/4 pt-2.5 text-xs">
                      <ul class="flex flex-col gap-3">
                        <li class="flex items-center justify-between text-primary/40">
                          <span>
                            {info.metrics_label}
                          </span>
                          <span>
                            {info.metrics_value_label}
                          </span>
                        </li>
                        {#each info.metrics || [] as metric}
                          <li class={cn('flex items-center justify-between pb-0')}>
                            <span>
                              {metric.name}
                            </span>
                            <span>
                              {metric.value}
                            </span>
                          </li>
                        {/each}
                      </ul>
                    </div>
                  </Accordion>
                </div>
              {/if}
            {/if}

            <Link
              class="block w-full h-full bg-background !rounded-md"
              title={alt}
              on:click={() => onPreviewMedia(mediaItem, i)}
            >
              <ShopifyImg
                id={i}
                {src}
                {alt}
                widths={[width]}
                sizes={buildSizes({
                  default: `${width}px`,
                  lg: '100vw'
                })}
                draggable="false"
                class={cn('h-full w-full bg-highlight/64 object-cover rounded-md')}
              />
            </Link>
          </div>
        {/if}

        {#if mediaItem && mediaItem.__typename === 'Video'}
          {@const src = getSrc(mediaItem.sources)}
          <div class="min-w-full select-none relative overflow-hidden rounded-md aspect-[7/9]">
            <video
              src={src + '#t=0.001'}
              preload="metadata"
              autoplay={true}
              loop
              muted
              playsinline
              class="min-w-full h-full object-cover cursor-pointer"
              on:click={() => onPreviewMedia(src, i)}
            />
          </div>
        {/if}
      {/each}
    {:else}
      {#each Array(4) as _}
        <Skeleton
          class={cn('min-w-full select-none relative overflow-hidden rounded-md aspect-[7/9]')}
        />
      {/each}
    {/if}
  </div>
{/key}

{#if selectedMedia}
  <div>
    <PreviewMedia
      {selectedMedia}
      on:close={() => onClosePreview()}
      on:next={() => handleMediaCarousel('right')}
      on:prev={() => handleMediaCarousel('left')}
      prevDisabled={selectedMediaIdx === 0}
      nextDisabled={selectedMediaIdx === parsedMedia.length - 1}
    />
  </div>
{/if}
